import { Box, CircularProgress, Divider, InputAdornment, Typography } from "@mui/material"
import { SearchOutlined } from "@mui-symbols-material/w300"
import type { FC, UIEvent } from "react"
import { useMemo, useState } from "react"
import type { Control } from "react-hook-form"
import { useForm } from "react-hook-form"

import { SALES_OPPORTUNITY_STATUS_TO_LABEL } from "../../constants"
import { useGetSalesOpportunityCustomerCompanies } from "../../hooks/useGetSalesOpportunityCustomerCompanies"
import type { SalesOpportunitySearchParams } from "../../schemas"

import { FilterCheckbox } from "@/components/common/Filters"
import { SearchFilterDrawer } from "@/components/common/SearchFilterDrawer/SearchFilterDrawer"
import { AerosDatePicker, AerosTextField } from "@/components/UiKit"
import { DEBOUNCE_DELAY_MS } from "@/constants/common"
import type { SalesPipelineStatusGql } from "@/graphql/codegen/graphql"
import useGetUsers from "@/screens/ChangeLog/hooks/useGetUsers"
import useDebounce from "@/utils/useDebounce"

interface FilterFormFields {
  companyIds: string[]
  createdByUserIds: string[]
  statuses: SalesPipelineStatusGql[]
}

interface SalesOpportunityFilterDrawerProps {
  open: boolean
  searchParams: SalesOpportunitySearchParams
  onToggle: () => void
  onChange: <K extends keyof SalesOpportunitySearchParams>(key: K, value: SalesOpportunitySearchParams[K]) => void
  onClear: () => void
}

export const SalesOpportunityFilterDrawer: FC<SalesOpportunityFilterDrawerProps> = ({
  open,
  searchParams,
  onToggle,
  onChange,
  onClear,
}) => {
  const companyIds = searchParams.companyIds ?? []
  const createdByUserIds = searchParams.createdByUserIds ?? []
  const statuses = searchParams.statuses ?? []

  const { control } = useForm<FilterFormFields>({
    defaultValues: { companyIds, createdByUserIds, statuses },
  })

  return (
    <SearchFilterDrawer open={open} toggleDrawer={onToggle} clear={onClear} testId='sales-opportunity.filter-drawer'>
      <Box className='flex h-full flex-col gap-y-4 overflow-y-auto pb-4'>
        <SalesOpportunityCustomerFilter
          control={control}
          values={companyIds}
          onChange={(values) => onChange("companyIds", values)}
        />
        <SalesOpportunityCreatedByFilter
          control={control}
          values={createdByUserIds}
          onChange={(values) => onChange("createdByUserIds", values)}
        />
        <SalesOpportunityQuoteDateFilter
          quoteDateFrom={searchParams.quoteDateFrom}
          quoteDateTo={searchParams.quoteDateTo}
          onFromChange={(value) => {
            onChange("quoteDateFrom", value ?? undefined)
            // Clearing "From" also clears "To" — BE DateRange requires `start`, so a To-only filter would silently no-op.
            if (!value) onChange("quoteDateTo", undefined)
          }}
          onToChange={(value) => onChange("quoteDateTo", value ?? undefined)}
        />
        <SalesOpportunityStatusFilter
          control={control}
          values={statuses}
          onChange={(values) => onChange("statuses", values)}
        />
      </Box>
    </SearchFilterDrawer>
  )
}

interface QuoteDateFilterProps {
  quoteDateFrom?: string
  quoteDateTo?: string
  onFromChange: (value: string | null) => void
  onToChange: (value: string | null) => void
}

const SalesOpportunityQuoteDateFilter: FC<QuoteDateFilterProps> = ({
  quoteDateFrom,
  quoteDateTo,
  onFromChange,
  onToChange,
}) => (
  <Box className='flex flex-col' data-testid='sales-opportunity.quote-date-filter'>
    <Typography variant='subtitle1' fontWeight={700} className='px-4 py-1.5 text-gray-700'>
      Quote
    </Typography>
    <Divider className='mb-4' />
    <Box className='flex flex-col gap-3 px-4'>
      <AerosDatePicker
        value={quoteDateFrom ?? null}
        onChange={onFromChange}
        label='From'
        data-testid='sales-opportunity.quote-date-filter.from-field'
      />
      <AerosDatePicker
        value={quoteDateTo ?? null}
        onChange={onToChange}
        label='To'
        disabled={!quoteDateFrom}
        data-testid='sales-opportunity.quote-date-filter.to-field'
      />
    </Box>
  </Box>
)

interface CustomerFilterProps {
  control: Control<FilterFormFields>
  values: string[]
  onChange: (values: string[]) => void
}

const SalesOpportunityCustomerFilter: FC<CustomerFilterProps> = ({ control, values, onChange }) => {
  const [searchTerm, setSearchTerm] = useState("")
  const debouncedSearch = useDebounce(searchTerm, DEBOUNCE_DELAY_MS)
  const { companies, isFetching, loadMore } = useGetSalesOpportunityCustomerCompanies({ query: debouncedSearch })

  const canLoadMore = (event: UIEvent) => {
    const el = event.currentTarget
    return !isFetching && el.scrollTop + el.clientHeight >= el.scrollHeight
  }

  return (
    <Box className='flex flex-col' data-testid='sales-opportunity.customer-filter'>
      <Typography variant='subtitle1' fontWeight={700} className='px-4 py-1.5 text-gray-700'>
        Customer
      </Typography>

      <Box className='flex-none p-4'>
        <AerosTextField
          size='small'
          placeholder='Search by Name'
          value={searchTerm}
          onChange={setSearchTerm}
          data-testid='sales-opportunity.customer-filter.search-field'
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchOutlined className='text-gray-600' />
                </InputAdornment>
              ),
              endAdornment: isFetching ? (
                <InputAdornment position='end'>
                  <CircularProgress size={20} />
                </InputAdornment>
              ) : undefined,
            },
          }}
        />
      </Box>

      <Box
        className='max-h-60 overflow-y-auto overflow-x-hidden px-8 py-2'
        onScroll={(event) => {
          if (canLoadMore(event)) loadMore()
        }}
      >
        {companies.map((company) => (
          <FilterCheckbox
            key={company.companyId}
            control={control}
            name='companyIds'
            value={company.companyId}
            values={values}
            label={company.name}
            onFilterChange={onChange}
            data-testid={`sales-opportunity.customer-filter.option-${company.companyId}`}
          />
        ))}
        {isFetching && (
          <Box className='flex justify-center py-2'>
            <CircularProgress size={24} />
          </Box>
        )}
      </Box>
    </Box>
  )
}

interface CreatedByFilterProps {
  control: Control<FilterFormFields>
  values: string[]
  onChange: (values: string[]) => void
}

const SalesOpportunityCreatedByFilter: FC<CreatedByFilterProps> = ({ control, values, onChange }) => {
  const [searchTerm, setSearchTerm] = useState("")
  const { userList, fetching } = useGetUsers({ query: "" })

  const filteredUsers = useMemo(() => {
    const normalized = searchTerm.trim().toLocaleLowerCase()
    const seen = new Set<string>()
    return userList
      .filter((user) => {
        if (!user.name || seen.has(user.userId)) return false
        if (normalized && !user.name.toLocaleLowerCase().includes(normalized)) return false
        seen.add(user.userId)
        return true
      })
      .map((user) => ({ value: user.userId, label: user.name }))
  }, [userList, searchTerm])

  return (
    <Box className='flex flex-col' data-testid='sales-opportunity.created-by-filter'>
      <Typography variant='subtitle1' fontWeight={700} className='px-4 py-1.5 text-gray-700'>
        Created By
      </Typography>

      <Box className='flex-none p-4'>
        <AerosTextField
          size='small'
          placeholder='Search by Name'
          value={searchTerm}
          onChange={setSearchTerm}
          data-testid='sales-opportunity.created-by-filter.search-field'
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchOutlined className='text-gray-600' />
                </InputAdornment>
              ),
              endAdornment: fetching ? (
                <InputAdornment position='end'>
                  <CircularProgress size={20} />
                </InputAdornment>
              ) : undefined,
            },
          }}
        />
      </Box>

      <Box className='max-h-60 overflow-y-auto px-8 py-2'>
        {filteredUsers.map((option) => (
          <FilterCheckbox
            key={option.value}
            control={control}
            name='createdByUserIds'
            value={option.value}
            values={values}
            label={option.label}
            onFilterChange={onChange}
            data-testid={`sales-opportunity.created-by-filter.option-${option.value}`}
          />
        ))}
        {fetching && (
          <Box className='flex justify-center py-2'>
            <CircularProgress size={24} />
          </Box>
        )}
      </Box>
    </Box>
  )
}

interface StatusFilterProps {
  control: Control<FilterFormFields>
  values: SalesPipelineStatusGql[]
  onChange: (values: SalesPipelineStatusGql[]) => void
}

const SalesOpportunityStatusFilter: FC<StatusFilterProps> = ({ control, values, onChange }) => (
  <Box className='flex flex-col' data-testid='sales-opportunity.status-filter'>
    <Typography variant='subtitle1' fontWeight={700} className='px-4 py-1.5 text-gray-700'>
      Status
    </Typography>
    <Divider className='mb-4' />
    <Box className='flex flex-col px-4'>
      {Object.entries(SALES_OPPORTUNITY_STATUS_TO_LABEL).map(([value, label]) => (
        <FilterCheckbox
          key={value}
          control={control}
          name='statuses'
          value={value}
          values={values}
          label={label}
          onFilterChange={(updated) => onChange(updated as SalesPipelineStatusGql[])}
          data-testid={`sales-opportunity.status-filter.option-${value}`}
        />
      ))}
    </Box>
  </Box>
)
